@use "@styles/variables";

// Constants
$cell-height: 64px;

// Keep in sync with `HEADER_ROW_PX` in calculateInitialVisibleRows.ts.
$header-height: 31px;
$cell-padding-block: 8px;

// Horizontal breathing room per cell; two adjacent cells therefore sit twice
// this far apart. Exposed as a custom property so a container can tighten the
// column gap without restyling every cell.
$cell-padding-inline: 12px;
$cell-padding: $cell-padding-block
    var(--view-table-cell-padding-inline, #{$cell-padding-inline});
$zindex-pinned: 3;
$zindex-headers: 4;

// The actively dragged row must float above all body content — pinned and
// focused cells top out at $zindex-headers (4) — so it never reflows behind a
// neighbouring row it is being dragged past.
$zindex-dragging-row: $zindex-headers + 1;

// Outer wrapper: establishes the positioning context for the focused-row
// indicator overlay so its left/right/top are relative to this element,
// not to the inner scroll container.
.tableOuter {
    position: relative;
    height: 100%;
    width: 100%;

    // Allow this flex child to shrink below its content size so that opening
    // the task detail panel on the right doesn't push the table instead of
    // shrinking it.
    min-width: 0;
    min-height: 0;

    &.hasFixedTopOffsetPx {
        height: calc(100vh - var(--fixed-top-offset-px, 0px));
        min-height: calc(100vh - var(--fixed-top-offset-px, 0px));
        max-height: calc(100vh - var(--fixed-top-offset-px, 0px));
    }
}

// Core container
.table-container {
    --border-color: #{variables.$misc-alpha-200};
    --view-table-nesting-indent: 0;

    position: relative;
    max-height: 100%;
    width: 100%;
    height: 100%;
    overflow: auto;
    border-top: 1px solid var(--border-color);
    display: flex;
    flex-direction: column;

    &:global(.overflow-left) {
        .last-left-column {
            border-right: 1px solid var(--border-color) !important;
        }
    }
    &:global(.overflow-right) {
        .first-right-column {
            border-left: 1px solid var(--border-color) !important;
        }
    }

    &.hasFixedTopOffsetPx {
        height: calc(100vh - var(--fixed-top-offset-px, 0px));
        min-height: calc(100vh - var(--fixed-top-offset-px, 0px));
        max-height: calc(100vh - var(--fixed-top-offset-px, 0px));
    }

    &.hideTopBorder {
        border-top: none;
    }
}

.bordered {
    border: 1px solid var(--border-color);
    border-radius: variables.$border-radius-lg;

    .lastVisibleRow .cell {
        border-bottom: none;
    }
}

.columnBorders {
    // Neither a filler nor the cell before one takes a border, or the line
    // lands in the blank space the filler holds open.
    .cell:not(
        :last-child,
        :global(.filler),
        :global(.filler-start),
        :has(+ :global(.filler))
    ),
    .header:not(
        :last-child,
        :global(.filler),
        :global(.filler-start),
        :has(+ :global(.filler))
    ) {
        border-right: 1px solid var(--border-color);
    }
}

.groupBoundary {
    border-right: 1px solid var(--border-color);
}

.table {
    border: none;

    &:not(.empty) {
        margin-bottom: auto;
    }

    &.hasFooter {
        padding-bottom: 32px;
    }
}

.headers {
    position: sticky;
    top: 0;
    background: white;
    z-index: $zindex-headers;

    .headerVariantMenuTrigger {
        opacity: 0;
    }

    &:hover .headerVariantMenuTrigger,
    .headerVariantMenuTrigger[data-popup-open] {
        opacity: 1;
    }

    &:hover .resizer {
        opacity: 1;
    }
}

// Header styles
.header {
    cursor: default;
    height: var(--view-table-header-height, #{$header-height});
    background-color: variables.$white;
    display: flex;
    align-items: center;
    padding: $cell-padding;
    white-space: nowrap;
    overflow: hidden;
    position: relative;

    &.pinned-left,
    &.pinned-right {
        z-index: $zindex-headers;

        &:where(:focus-within) {
            z-index: $zindex-headers + 1;
        }
    }
}

.alignRight {
    justify-content: flex-end;
}

.groupHeader {
    justify-content: center;
    font-weight: 500;
    text-align: center;

    // `clip` (not `hidden`) so the sticky label resolves against the outer
    // scroll container rather than this cell.
    overflow: clip;
}

// A leaf lifted to its true tier (headerTierDepth) renders its label in the
// placeholder cell at that tier, sitting on the same row as sibling group
// headers. Match their weight so every label on a tier row reads uniformly —
// a plain leaf header cell would otherwise inherit the lighter default weight.
.header[data-header-tier-label] {
    font-weight: 500;
}

.groupHeaderLabel {
    position: sticky;
    left: calc(var(--view-table-pinned-left-width, 0px) + 12px);
    right: calc(var(--view-table-pinned-right-width, 0px) + 12px);
    max-width: 100%;
}

// A pinned group never scrolls, so its label centres instead of chasing the
// scroll edge past the other pinned columns.
.pinnedGroupHeaderLabel {
    position: static;
}

.groupPlaceholder {
    pointer-events: none;
}

.cell {
    background-color: variables.$white;
    padding: $cell-padding;
    display: flex;
    align-items: center;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    &.pinned-left,
    &.pinned-right {
        z-index: $zindex-pinned;
        background-color: variables.$white;

        &:where(:focus-within) {
            // Forces the cell with focus to display above other cells (ie. actions dropdowns)
            z-index: $zindex-pinned + 1;
        }
    }
}

.cell,
.header {
    &:first-child {
        padding-left: var(
            --view-table-edge-padding-inline,
            var(--page-padding-inline)
        );
    }
    &:last-child {
        padding-right: var(
            --view-table-edge-padding-inline,
            var(--page-padding-inline)
        );
    }

    // A filler holds no content and has to collapse to nothing once the table
    // runs out of width. Must stay below the rules above, which match it at
    // equal specificity as the first or last cell and would win on order.
    &:global(.filler),
    &:global(.filler-start) {
        padding: 0;
    }

    border-bottom: 1px solid var(--border-color);
}

// Where the selection checkbox sits relative to the table's left edge. A
// container can line it up with its own chrome by setting the custom property,
// which inherits — the class names here are hashed, so reaching in from
// outside is not an option. Must stay below the `:first-child` rule above: the
// selection column is pinned first, so that rule matches it at equal
// specificity and would otherwise win on source order (hence the default
// repeats the page gutter).
.cell.selectColumn,
.header.selectColumn {
    padding-left: var(
        --view-table-select-padding-left,
        var(--page-padding-inline)
    );
}

// The row checkbox is wrapped for its shift-click handler; the header one isn't.
// Without this the wrapper is a block box around an inline-flex label, so the
// label sits on a text baseline and lands a pixel off the header's.
.selectCheckboxWrapper {
    display: flex;
}

// ─── Expandable-section border rules ─────────────────────────────────────────
// Remove the bottom border from a parent row when it is expanded so its
// children visually "attach" without a dividing line.
.expandedRow .cell {
    border-bottom: 1px solid transparent;
}

// Sub-rows (depth > 0, children of an expanded section) carry no borders
// except on the last sub-row in each section (class-based, since virtual spacer
// divs break positional pseudo-selectors like :last-child and +).
.subRow:not(.lastSubRow) .cell {
    border-bottom: none;
}

// ─────────────────────────────────────────────────────────────────────────────

.row {
    cursor: default;

    .showOnFocus,
    .showOnHover,
    .showOnCellHover {
        opacity: 0;

        // A hover-revealed popup trigger (a `Menu.More` kebab), or a wrapper
        // around one, stays visible while its popup is open, after the
        // pointer has left the row.
        &[data-popup-open],
        &:has([data-popup-open]) {
            opacity: 1;
        }
    }

    .hideOnHover {
        opacity: 1;
    }

    .forceShow {
        opacity: 1;
    }

    &.focused-row .cell {
        .showOnFocus {
            opacity: 1;
        }
    }

    &.clickable-row {
        cursor: pointer;
        &.bulk-selected .cell {
            background-color: variables.$blue-50;
            .cellSelectionCheckbox {
                opacity: 1;
            }
        }

        &.active-row .cell {
            background-color: variables.$blue-50;
            .cell-add-button,
            [data-picker-cell-add] {
                opacity: 1;
            }
            .cellSelectionCheckbox {
                opacity: 1;
            }
        }

        &.focused-row {
            &:focus-within {
                .cell:first-child {
                    border-left-color: transparent;
                }
            }
        }

        &.focused-row .cell {
            .cell-add-button,
            [data-picker-cell-add] {
                opacity: 1;
            }
            .cellSelectionCheckbox {
                opacity: 1;
                transition: opacity 50ms ease-out;
            }
        }
    }

    // Opt-in highlight (e.g. occurrences due to apply today). Kept outside
    // `.clickable-row` so read-only tables can use it too.
    &:global(.view-table-row-highlighted) .cell {
        background-color: variables.$blue-50;
    }

    // Use CSS :hover instead of React state to avoid re-rendering all rows on hover.
    &:hover:not(.active-row):not(:global(.view-table-row-highlighted)) .cell {
        background-color: variables.$gray-50;

        // `Picker.CellContent`'s empty "Add" follows the legacy add button.
        .cell-add-button,
        [data-picker-cell-add] {
            opacity: 1;
        }
        .taskTitleCell {
            border: 1px solid variables.$misc-alpha-300;

            &:hover {
                border-color: variables.$gray-400;
            }
        }
        .cellSelectionCheckbox {
            opacity: 1;
        }

        .showOnHover {
            opacity: 1;
        }
        .hideOnHover {
            opacity: 0;
        }
    }

    .cell:hover .showOnCellHover {
        opacity: 1;
    }

    &.no-border {
        border: none;
    }
}

// The dragged row is a grid item with a transform (its own stacking context);
// without an explicit z-index it paints in DOM order — above earlier rows but
// behind later ones. Lift it so it is always on top of the rows it moves past.
.draggingRow {
    z-index: $zindex-dragging-row;
}

.draggingRow .cell {
    background-color: variables.$gray-50;
}

// Every row in the active drag's moving set (a multi-selection or a group +
// descendants) is lifted/dimmed so the whole set reads as being picked up,
// while only the native active row also translates with the cursor.
.dragSetRow .cell {
    background-color: variables.$gray-50;
    opacity: 0.5;
}

.dragInProgress {
    cursor: grabbing;
}

// While dragging over an invalid drop target, force a not-allowed cursor across
// the whole table (overriding the per-row grab/pointer cursors) so it's clear
// the drop won't land. Scoped to the active blocked-hover state only.
.dropNotAllowed {
    &,
    * {
        cursor: not-allowed !important;
    }
}

// Drop indicators for row drag-and-drop: an insertion line above/below the
// over-row for a sibling drop, or a ring around it for a nest ("on") drop.
.dropBefore,
.dropAfter,
.dropOn {
    position: relative;
}

.dropBefore::after,
.dropAfter::after {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    height: variables.rem(2px);
    background-color: variables.$blue-500;
    z-index: 3;
    pointer-events: none;
}

.dropBefore::after {
    top: 0;
}

.dropAfter::after {
    bottom: 0;
}

.dropOn::after {
    content: "";
    position: absolute;
    inset: 0;
    border: variables.rem(2px) solid variables.$blue-500;
    border-radius: variables.rem(4px);
    z-index: 3;
    pointer-events: none;
}

// Blocked drop: recolour the insertion line / nest ring to the danger colour so
// the user can see the target the drop resolves to while signalling it won't
// land (paired with the not-allowed cursor above).
.dropBlocked {
    &.dropBefore::after,
    &.dropAfter::after {
        background-color: variables.$red-500;
    }

    &.dropOn::after {
        border-color: variables.$red-500;
    }
}

.keyboardNavigationEnabled {
    .header.selectColumn,
    .selectColumn {
        overflow: visible;
    }
}

.blankView {
    width: 100%;
    height: 100%;
    position: sticky;
    top: 0;
    left: 0;
    padding: 24px;
    display: flex;
    justify-content: center;
    align-items: center;
    margin-bottom: auto;
}

.selectCheckbox {
    height: 26px;
    position: relative;

    &.rowSelect {
        opacity: 0;

        &:focus-within {
            opacity: 1;
        }
    }

    // Selecting is a primary action on this table, not a hover shortcut.
    .alwaysShowSelection &.rowSelect {
        opacity: 1;
    }

    &:after {
        content: "";
        z-index: -1;
        cursor: pointer;
        position: absolute;
        top: -16px;
        left: -24px;
        right: -16px;
        bottom: -16px;
    }
}

// Spacers that hold the height of off-screen virtual rows.
// They span all grid columns so CSS subgrid column alignment is preserved.
.virtualSpacer {
    grid-column: 1 / -1;
}

// The single cell of a full-width row, spanning every column — for
// group-level affordances (load-more / error rows) that don't map onto
// table columns.
//
// Carries the row divider itself. Sub-row borders are suppressed except on
// `.lastSubRow`, which is keyed off `.cell` — a full-width row has no `.cell`,
// so a group whose last row is load-more/error would otherwise run straight
// into the next group header with no line between them.
.fullWidthCell {
    grid-column: 1 / -1;
    padding: var(--cell-padding);
    border-bottom: 1px solid var(--border-color);

    &:hover {
        background-color: var(--bg-hover-subtle);
    }
}

.resizer {
    opacity: 0;
    z-index: 3;
    cursor: col-resize;
    position: absolute;
    right: -2px;
    top: 50%;
    transform: translateY(-50%);
    touch-action: none;
    user-select: none;
    width: 5px;
    height: 16px;
    margin-left: 0 !important;

    &::after {
        content: "";
        position: absolute;
        left: 2px;
        top: 0;
        width: 1px;
        height: 100%;
        background: variables.$misc-alpha-200;
    }

    &.isResizing {
        opacity: 1;

        &::after {
            background: variables.$blue-500;
        }
    }
}

// Clip wrapper: positioned at the header's bottom edge (set via inline style in
// the component). overflow:hidden clips the indicator when it scrolls into the
// header area, preventing the blue border from painting over the sticky header.
.focusedRowIndicatorClip {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    overflow: hidden;
    pointer-events: none;

    // Above headers ($zindex-headers: 4) so the indicator border isn't covered
    // by sticky header cells. The clip boundary keeps it out of the header area.
    z-index: $zindex-headers + 1;
}

.focusedRowIndicator {
    position: absolute;
    left: 0;
    right: 0;
    pointer-events: none;
    border: 1px solid variables.$blue-300;
    background: transparent;

    // Only animate keyboard focus moves. Scroll updates strip this class so the
    // indicator tracks the row 1:1 without lagging behind scrollTop.
    &.animatePosition {
        transition:
            top 50ms ease-out,
            height 50ms ease-out;
    }
}
